import { Page } from '../base.js';
import TIML from '../../../framework/timl.js';
import {
  Breadcrumbs,
  FormButton,
  FloatingLabelInput,
  FloatingLabelTextarea,
  FloatingLabelSelect
} from '../../components.js';

export class DefaultListRow {
  static props = ['href', 'name', 'createdAt', 'userEmail'];
  static timl = `
    td
      a[href="{{ Props.href }}"] {{ Props.name }}
    td.pr-4[style="white-space:nowrap;"] {{ Props.createdAt }}
    td {{ Props.userEmail }}
  `;
}

export class ShowPage extends Page {
  static css = `
    .details-table {
      margin-top: 0;
    }

    .details-table tbody tr th,
    .details-table tbody tr td {
      text-align: left;
      vertical-align: top;
      padding: 14px 0;
      border-bottom: 1px solid #e5e7eb;
    }

    .details-table tbody tr th {
      width: 250px;
      color: #555;
      font-weight: normal;
      padding-right: 24px;
    }

    .details-table tbody tr td {
      color: #7d7d7d;
    }
  `;

  static timlComponents = {
    Breadcrumbs
  };

  static javascript() {
    if (location.hash) {
      document.querySelector('#name').innerText = decodeURIComponent(location.hash.substring(1));
    }
  }
}

export class EditPage extends Page {
  static timlComponents = {
    Breadcrumbs,
    FormButton,
    FloatingLabelInput,
    FloatingLabelTextarea,
    FloatingLabelSelect
  };
}

export class DeletePage extends Page {
  static timlComponents = {
    Breadcrumbs,
    FormButton
  };
}

export function appendDataToListTable(selectorOrItems, itemsOrRowComponent, rowComponentOrMapItemToRowData, maybeMapItemToRowData) {
  const selector = typeof selectorOrItems === 'string' ? selectorOrItems : 'tbody';
  const items = typeof selectorOrItems === 'string' ? itemsOrRowComponent : selectorOrItems;
  const rowComponent = typeof selectorOrItems === 'string' ? rowComponentOrMapItemToRowData : itemsOrRowComponent;
  const mapItemToRowData = typeof selectorOrItems === 'string' ? maybeMapItemToRowData : rowComponentOrMapItemToRowData;

  const tbody = document.querySelector(selector);
  if (!tbody) return;

  tbody.replaceChildren();
  tbody.classList.add('in');
  if (!items?.length) {
    renderListState(tbody, 'empty');
    return;
  }

  const frag = document.createDocumentFragment();

  items.forEach((item, i) => {
    const tr = document.createElement("tr");
    tr.className = "fade";
    tr.style.transitionDelay = `${i * 50}ms`;
    const props = mapItemToRowData(item);
    if (props?.href) {
      tr.dataset.href = props.href;
      tr.classList.add('clickable-row');
    }
    const rowFragment = TIML.renderComponent(rowComponent, props);
    tr.appendChild(rowFragment);
    frag.appendChild(tr);
  });

  tbody.appendChild(frag);
  requestAnimationFrame(() => {
    tbody.querySelectorAll('tr.fade').forEach(tr => tr.classList.add('in'));
  });
}

function emptyStateIcon() {
  return `
    <svg viewBox="0 0 92 92" aria-hidden="true">
      <rect x="19" y="10" width="48" height="65" rx="7"></rect>
      <rect x="31" y="26" width="6" height="6" rx="1"></rect>
      <path d="M43 29h15M31 44h5M43 46h15M31 60h5M43 62h11"></path>
      <circle cx="66" cy="67" r="16"></circle>
      <path d="M66 58v18M57 67h18"></path>
    </svg>
  `;
}

function renderListState(tbody, state, error, sectionTitle) {
  const table = tbody.closest('table');
  if (!table) return;

  tbody.replaceChildren();
  tbody.classList.add('in');

  const row = document.createElement('tr');
  row.className = `list-state-row list-state-row--${state}`;
  const cell = document.createElement('td');
  cell.colSpan = Math.max(table.querySelectorAll('thead th').length, 1);

  const content = document.createElement('div');
  content.className = `list-state list-state--${state}`;
  content.setAttribute('role', state === 'error' ? 'alert' : 'status');

  const icon = document.createElement('div');
  icon.className = 'list-state-icon';
  icon.innerHTML = emptyStateIcon();

  const title = document.createElement('h2');
  const description = document.createElement('p');

  if (state === 'error') {
    title.textContent = `Unable to load ${String(sectionTitle || 'this page').toLowerCase()}`;
    description.textContent = error?.code === 'suite_bootstrap_failed'
      ? error.message
      : error?.status === 501
        ? 'This section is not implemented by the current API yet (501).'
        : 'Something went wrong while loading this section. Please try again.';
  } else {
    title.textContent = table.dataset.emptyTitle || 'Nothing here yet';
    description.textContent = table.dataset.emptyDescription || '';
  }

  content.append(icon, title, description);

  if (state === 'empty' && table.dataset.emptyAction && table.dataset.emptyHref) {
    const action = document.createElement('a');
    action.className = 'list-state-action';
    const languageCode = globalThis.Translations?.langCode;
    action.href = table.dataset.emptyHref.startsWith('/') && languageCode
      ? `/${languageCode}${table.dataset.emptyHref}`
      : table.dataset.emptyHref;
    action.textContent = table.dataset.emptyAction;
    content.appendChild(action);
  } else if (state === 'error' && error?.code === 'suite_bootstrap_failed') {
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.className = 'list-state-action';
    retry.textContent = 'Retry account setup';
    retry.onclick = async () => {
      retry.disabled = true;
      retry.textContent = 'Retrying...';
      try {
        await Auth.ensureSuiteBootstrap();
        TIML.loadPageContent();
      } catch (retryError) {
        description.textContent = retryError?.message ||
          'We still could not finish setting up your account. Please try again.';
        retry.disabled = false;
        retry.textContent = 'Retry account setup';
      }
    };
    content.appendChild(retry);
  }

  cell.appendChild(content);
  row.appendChild(cell);
  tbody.appendChild(row);
}

export function renderListError(selector, error, sectionTitle) {
  const tbody = document.querySelector(selector);
  if (!tbody) return;
  renderListState(tbody, 'error', error, sectionTitle);
}

export function populateEditForm(data) {
  console.log('populateEditForm(data)', data);
  let id = "";
  let slug = "";

  for (const [key, value] of Object.entries(data)) {
    const field = document.querySelector(`[name="${key}"]`);
    if (field) {
      if (field.type === 'checkbox') {
        field.checked = value === true || value === 'true' || value === 1 || value === '1';
      } else {
        field.value = value;
      }
      if (value) {
        setTimeout(function(){field.focus()}, 10);
      }

      if (key === "id") {
        id = value;
      } else if (key === "slug") {
        slug = value;
      }
    }
  }

  const shareButton = document.querySelector('#share-button');
  if (shareButton) {
    shareButton.href = shareButton.href + (slug || id);
  }
}
